import { Dispatch, SetStateAction } from "react";

import TerritoryLevel from "@mapmycustomers/shared/enum/TerritoryLevel";
import AreaRegion from "@mapmycustomers/shared/types/territory/AreaRegion";
import LookupData from "@mapmycustomers/shared/types/territory/LookupData";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

const useToggleHandler = (
  baseLevel: TerritoryLevel,
  currentLevel: TerritoryLevel,
  lookupData: LookupData,
  setSelectedRegionIds: Dispatch<SetStateAction<AreaRegion["id"][]>>
) =>
  useDynamicCallback((regionId: AreaRegion["id"], shouldAdd?: boolean) => {
    const actualRegionIds: AreaRegion["id"][] =
      baseLevel === currentLevel
        ? [regionId]
        : // We know that level is never equal to ADM1 (because there's no level less than adm1,
          // hence currentLevel === level in such case)
          // @ts-ignore
          lookupData[currentLevel as TerritoryLevel.ADM1 | TerritoryLevel.ADM2][regionId]?.[
            baseLevel
          ] ?? [];

    setSelectedRegionIds((ids: AreaRegion["id"][]) => {
      const currentIds = new Set(ids);
      const alreadySelected =
        shouldAdd === undefined ? actualRegionIds.every((id) => currentIds.has(id)) : !shouldAdd;
      if (alreadySelected) {
        const actualRegionIdsSet = new Set(actualRegionIds);
        // Logic below is disabled due to a request from product, but I still wanted to keep it
        // in the code in a commented out form. So that at least you are aware of such logic.
        //
        // // This IF handles a tricky logic: only exclude some ids if these ids are not selected as a part of
        // // other higher region.
        // // Imagine we have County A and B. A consists of postal codes X and Y. B consists of Y and Z.
        // // So they share postal code Y.
        // // Precondition: all X, Y, and Z are currently selected.
        // // Now user clicks on county A. actualRegionIds would be equal to X and Y.
        // // However, we don't really want to remove Y from selection, because it is still selected
        // // as a part of county B. So we remove Y from actualRegionIds.
        // //
        // // In the logic below we do several things:
        // // - iterate over counties and look for those of them, which are currently selected (only B
        // //   would be found for the example above, because we exclude A itself from the search
        // //   using filter((id) => id !== regionId) condition). We don't care if they're fully selected
        // //   or not, partial selection is also fine.
        // // - if such regions are found, we iterate over all their sub-regions and exclude them
        // //   from removal. For example above, we'd try to remove both Y and Z regions from actualRegionIds.
        // // Note: same logic is valid for state/postal code and state/county pairs too.
        // if (level !== currentLevel) {
        //   Object.keys(lookupData[currentLevel as TerritoryLevel.ADM1 | TerritoryLevel.ADM2])
        //     .filter((id) => id !== regionId)
        //     .forEach((id) => {
        //       const subRegions: AreaRegion["id"][] =
        //         // @ts-ignore
        //         lookupData[currentLevel as TerritoryLevel.ADM1 | TerritoryLevel.ADM2][id]?.[
        //           level
        //         ] ?? [];
        //       const includesIds = subRegions.some((regionId) => actualRegionIdsSet.has(regionId));
        //       if (includesIds) {
        //         subRegions.forEach((regionId) => {
        //           actualRegionIdsSet.delete(regionId);
        //         });
        //       }
        //     });
        // }
        return ids.filter((id) => !actualRegionIdsSet.has(id));
      } else {
        return Array.from(new Set([...actualRegionIds, ...ids]));
      }
    });
  });

export default useToggleHandler;
